import React from 'react';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Skeleton from '../../components/ui/Skeleton/Skeleton';
import { useLayout } from '../../layout/LayoutContext';
import {
  SearchIcon,
  FilterIcon,
  FileReportIcon,
  ChevronDownIcon,
} from '../../components/ui/Icons';
import { useLoadsData } from './useLoadsData';
import { LoadsFilters, EMPTY_FILTERS, activeFilterCount } from './loadsFilters';
import LoadsStatusCards from './components/LoadsStatusCards';
import LoadsTable from './components/LoadsTable';
import Pager from './components/Pager';
import FiltersModal from './components/FiltersModal';
import ReportModal from './components/ReportModal';
import styles from './Loads.module.css';

const PAGE_SIZES = [10, 25, 50, 100];

const Loads: React.FC = () => {
  const { setHeaderContent } = useLayout();

  const [tab, setTab] = React.useState<'all' | 'critical'>('all');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [sizeMenuOpen, setSizeMenuOpen] = React.useState(false);

  // Selección de status (filtros rápidos acumulativos vía las cards)
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);
  // Filtros del modal
  const [filters, setFilters] = React.useState<LoadsFilters>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [reportOpen, setReportOpen] = React.useState(false);

  /**
   * 🔑 **Todo el filtrado y la paginación ocurren en el servidor.**
   *
   * Antes esta pantalla traía TODAS las cargas y filtraba, contaba y paginaba en el navegador. Eran
   * ~20 columnas por fila con cuatro tablas unidas, traídas desde Oregón, para mostrar 25 renglones.
   *
   * ⚠️ Por eso el estado de los filtros **entra al hook** en lugar de aplicarse a un arreglo local:
   * lo que se ve es exactamente lo que respondió el servidor. Filtrar aquí encima produciría el error
   * clásico de la paginación mal hecha — buscar y encontrar solo las coincidencias de la página que
   * se está viendo.
   */
  const {
    loads,
    total,
    totalPages,
    statusCounts,
    criticalCount,
    options,
    loading,
    search,
    setSearch,
    generating,
    generateReport,
    activeFilters,
  } = useLoadsData({
    page,
    pageSize,
    critical: tab === 'critical',
    selectedStatuses,
    filters,
  });

  const toggleStatus = React.useCallback((key: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);
  const clearStatuses = React.useCallback(() => setSelectedStatuses([]), []);

  /**
   * Firma de los filtros que afectan los conteos. Cada cambio incrementa el
   * pulso, que las cards usan para re-animar y hacer evidente el cambio.
   */
  const filterSignature = `${tab}|${search}|${JSON.stringify(filters)}`;
  const [pulse, setPulse] = React.useState(0);
  const firstRun = React.useRef(true);
  React.useEffect(() => {
    // No animar en el montaje inicial, solo en cambios reales del usuario.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPulse((p) => p + 1);
  }, [filterSignature]);

  const filterCount = activeFilterCount(filters);

  /**
   * Los conteos vienen del backend, calculados con los MISMOS filtros que la tabla.
   *
   * 📌 Antes se derivaban del arreglo completo en el navegador precisamente porque el endpoint de
   * conteos solo entendía la mitad de los filtros. Ya entiende todos —usa el mismo constructor de
   * `where` que la tabla— así que derivarlos aquí sería contar dos veces y arriesgar que no
   * coincidan.
   */
  const cardCounts = statusCounts;

  // Las status cards (clicables) ocupan el header en esta pantalla
  React.useEffect(() => {
    setHeaderContent(
      <LoadsStatusCards
        counts={cardCounts}
        selected={selectedStatuses}
        onToggle={toggleStatus}
        onClear={clearStatuses}
        pulseKey={pulse}
        filtered={filterCount > 0 || tab === 'critical'}
      />
    );
    return () => setHeaderContent(null);
  }, [
    cardCounts,
    selectedStatuses,
    toggleStatus,
    clearStatuses,
    setHeaderContent,
    pulse,
    filterCount,
    tab,
  ]);

  /**
   * ⚠️ La página se acota contra el total que reportó el SERVIDOR.
   *
   * Hace falta porque el total puede encogerse entre dos peticiones —alguien aplica un filtro
   * estando en la página 7— y pedir una página que ya no existe devuelve una tabla vacía sin
   * explicación.
   */
  const currentPage = Math.min(page, totalPages);

  // Reset de página al cambiar tab, tamaño, búsqueda o filtros
  React.useEffect(() => {
    setPage(1);
  }, [tab, pageSize, search, selectedStatuses, filters]);

  // Firma de la vista: cualquier cambio re-monta la tabla y re-anima las filas
  const viewKey = `${tab}|${currentPage}|${pageSize}|${search}|${selectedStatuses.join(',')}|${JSON.stringify(filters)}`;

  return (
    <Card className="dylo-animate-in">
      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={[styles.tab, tab === 'all' ? styles.tabActive : ''].join(' ')}
          onClick={() => setTab('all')}
        >
          Loads
        </button>
        <button
          type="button"
          className={[
            styles.tab,
            styles.tabCritical,
            tab === 'critical' ? styles.tabActive : '',
          ].join(' ')}
          onClick={() => setTab('critical')}
        >
          Critical Loads
          {criticalCount > 0 && <span className={styles.badge}>{criticalCount}</span>}
        </button>
      </div>

      {/* Toolbar */}
      <div
        className={[styles.toolbar, filterCount > 0 ? styles.toolbarFiltered : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.search}>
          <Input
            icon={<SearchIcon />}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={styles.toolBtn}
          onClick={() => setFiltersOpen(true)}
        >
          <FilterIcon size={16} />
          Filter
          {filterCount > 0 && <span className={styles.filterCount}>{filterCount}</span>}
        </button>

        {/* Abre el paso previo: los filtros que van a viajar y cuál de los dos reportes bajar (punto 8). */}
        <button
          type="button"
          className={styles.toolBtn}
          onClick={() => setReportOpen(true)}
          disabled={generating !== null}
        >
          <FileReportIcon size={16} />
          {generating ? 'Generating...' : 'Generate Report'}
        </button>

        <div className={styles.spacer} />

        {/*
          El total lo reporta el servidor.

          📌 Antes no hacía falta mostrarlo: la tabla tenía todas las filas y el número de páginas ya
          daba una idea del tamaño. Ahora el navegador solo ve una página, así que sin este dato no hay
          forma de saber cuántas cargas cumplen el filtro.
        */}
        {!loading && (
          <span className={styles.total}>
            {total.toLocaleString()} {total === 1 ? 'load' : 'loads'}
          </span>
        )}

        <Pager page={currentPage} totalPages={totalPages} onChange={setPage} />

        <div className={styles.pageSize}>
          <button
            type="button"
            className={styles.pageSizeBtn}
            onClick={() => setSizeMenuOpen((o) => !o)}
          >
            {pageSize} | Page
            <ChevronDownIcon size={15} />
          </button>
          {sizeMenuOpen && (
            <div className={styles.pageSizeMenu}>
              {PAGE_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={[
                    styles.pageSizeOption,
                    size === pageSize ? styles.pageSizeOptionActive : '',
                  ].join(' ')}
                  onClick={() => {
                    setPageSize(size);
                    setSizeMenuOpen(false);
                  }}
                >
                  {size} | Page
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className={`${styles.skeletonRows} dylo-animate-in`}>
          <Skeleton height={38} radius={8} />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={44} radius={8} />
          ))}
        </div>
      ) : (
        <div key={viewKey} className={styles.tableSwap}>
          <LoadsTable data={loads} />
        </div>
      )}

      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        options={options}
        filters={filters}
        onApply={setFilters}
      />

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        filters={activeFilters}
        total={total}
        generating={generating}
        onGenerate={generateReport}
      />
    </Card>
  );
};

export default Loads;
